import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../translations";
import zxcvbn from "zxcvbn";
import { API_BASE } from '../config';
import {
  PanelContainer, PageHeader, HeaderActions, Title,
  PanelCard, FieldLabel, RequiredMark, TextInput,
  PrimaryButton, SecondaryButton,
} from "../ui/common";
import { CheckIcon, XIcon, InfoIcon, MailIcon, LockIcon, UserIcon, ArrowRightIcon } from "../ui/icons";
import { Banner } from "../components/ui/Banner";
import { useToast } from "../components/ui/Toast";
import { scrollToTop } from "../utils/scrollToTop";
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// Ikony bez odpowiednika we wspólnym zestawie (src/ui/icons.js)
const UserPlusIcon = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PhoneIcon = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
`;

const BackIcon = styled(ArrowRightIcon)`
  transform: rotate(180deg);
`;

const Panel = styled.form`
  display: grid;
  gap: var(--sp-5);
`;

const FormGrid = styled.div`
  display: grid;
  gap: var(--sp-5);

  @media(min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled(FieldLabel)`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
`;

const PasswordStrength = styled.div`
  margin-top: var(--sp-3);
`;

const StrengthLabel = styled.div`
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--${p => p.$tone});
  margin-bottom: var(--sp-2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StrengthBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--border);
  border-radius: var(--r-sm);
  overflow: hidden;
  margin-bottom: var(--sp-2);
`;

const StrengthFill = styled.div`
  height: 100%;
  width: ${p => p.width}%;
  background: var(--${p => p.$tone});
  border-radius: var(--r-sm);
  transition: width var(--dur-slow) var(--ease);
`;

const StrengthTips = styled.div`
  font-size: var(--fs-xs);
  color: var(--text-muted);
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
`;

const Checklist = styled.div`
  margin-top: var(--sp-3);
  padding: var(--sp-3);
  background: var(--info-bg);
  border-radius: var(--r-md);
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-base);
  color: ${p => (p['data-ok'] ? 'var(--success)' : 'var(--danger)')};

  svg {
    flex-shrink: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--sp-3);
  justify-content: flex-end;
  flex-wrap: wrap;

  @media(max-width: 768px) {
    justify-content: stretch;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-top: var(--sp-2);
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
`;

const CheckboxLabel = styled.span`
  font-size: var(--fs-base);
  color: var(--text-muted);
  font-weight: 500;
`;

function meetsPasswordPolicy(s) {
  const pw = String(s || '');
  const minLen = 8;
  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  const lenOk = pw.length >= minLen;
  return {
    ok: lenOk && hasUpper && hasLower && hasDigit,
    details: { lenOk, hasUpper, hasLower, hasDigit, minLen }
  };
}

// Siła hasła (0-4 z zxcvbn) sprowadzona do trzech tonów tokenowych — tokeny znaczeniowe
// dają tylko success/warning/danger/info, więc pięciu odrębnych kolorów się nie da zachować.
function strengthTone(score) {
  if (score >= 3) return 'success';
  if (score === 2) return 'warning';
  return 'danger';
}

export default function AddUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const isDemo = localStorage.getItem('isDemo') === '1';

  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
    isadmin: false
  });

  // password strength
  const passEval = useMemo(() => zxcvbn(form.password || ''), [form.password]);
  const passScore = passEval.score ?? 0;
  const passPercent = Math.round((passScore / 4) * 100);
  const passTone = strengthTone(passScore);

  const policyCheck = useMemo(() => meetsPasswordPolicy(form.password), [form.password]);

  const isFormValid = useMemo(() => {
    return (
      form.firstname &&
      form.lastname &&
      form.email &&
      form.password &&
      form.confirmPassword &&
      form.password === form.confirmPassword &&
      policyCheck.ok && // hasło musi spełniać wszystkie wymagania
      passScore >= 3 // minimalnie "dobry" (score 3 lub 4)
    );
  }, [form, policyCheck, passScore]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Basic validation
    if (!form.firstname || !form.lastname || !form.email || !form.password) {
      setMessage({ type: 'error', text: t('addUser.fillRequired') });
      scrollToTop();
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMessage({ type: 'error', text: t('addUser.passwordMismatch') });
      scrollToTop();
      return;
    }

    // Sprawdź politykę hasła
    if (!policyCheck.ok) {
      setMessage({ type: 'error', text: t('addUser.passwordPolicyNotMet') });
      scrollToTop();
      return;
    }

    // Sprawdź siłę hasła
    if (passScore < 3) {
      setMessage({ type: 'error', text: t('addUser.passwordTooWeak') });
      scrollToTop();
      return;
    }

    // Normalize phone number: remove non-digits, strip country code 48 or leading 0 if present
    const rawPhone = form.phone_number || '';
    const digits = rawPhone.replace(/\D/g, '');
    let phoneForApi = digits;

    if (digits.length === 11 && digits.startsWith('48')) {
      phoneForApi = digits.slice(2); // +48XXXXXXXXX -> XXXXXXXXX
    } else if (digits.length === 10 && digits.startsWith('0')) {
      phoneForApi = digits.slice(1); // 0XXXXXXXXX -> XXXXXXXXX
    }

    // If phone provided, validate it's 9 digits (backend expects 9 digits)
    if (digits.length > 0 && phoneForApi.length !== 9) {
      setMessage({ type: 'error', text: t('addUser.invalidPhone') });
      scrollToTop();
      return;
    }

    setLoading(true);

    try {
      // payload zgodny z wymaganiami backendu
      const payload = {
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        phone_number: phoneForApi || '',
        // backend oczekuje pola "login" i "passwo"
        // używamy email jako login — zmień jeśli backend wymaga innego loginu
        login: form.email,
        passwo: form.password,
        // backend sprawdza is_admin (liczbę) — wyślij jako 0/1
        is_admin: form.isadmin ? 1 : 0
      };
      const csrf = localStorage.getItem('csrfToken') || '';
      const response = await fetch(`${API_BASE}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errMsg = `HTTP ${response.status} ${response.statusText}`;
        try {
          const json = await response.json();
          if (json) {
            if (typeof json === 'string') errMsg = json;
            else if (json.message) errMsg = json.message;
            else if (json.error) errMsg = json.error;
            else if (json.errors) errMsg = Array.isArray(json.errors) ? json.errors.join(', ') : JSON.stringify(json.errors);
            else errMsg = JSON.stringify(json);
          }
        } catch {
          try { const text = await response.text(); if (text) errMsg = text; } catch {}
        }
        throw new Error(errMsg);
      }

      toast.success(t('addUser.success'));
      setTimeout(() => navigate('/users'), 1000);
    } catch (error) {
      const text = (error && error.message) ? error.message : t('addUser.error');
      setMessage({ type: 'error', text });
      scrollToTop();
      toast.error(text);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PanelContainer>
      <PageHeader>
        <TitleRow>
          <UserPlusIcon size={28} />
          <Title style={{ margin: 0 }}>{t('users.addUser')}</Title>
        </TitleRow>
        <HeaderActions>
          <SecondaryButton type="button" onClick={() => navigate('/users')}>
            <BackIcon size={16} />
            {t('common.back')}
          </SecondaryButton>
        </HeaderActions>
      </PageHeader>
      <TylkoOdczytWPrzerwie>

      {isDemo && (
        <Banner tone="warning" title={t('addUser.demoDisabled')} />
      )}

      {message && (
        <Banner tone={message.type === 'error' ? 'danger' : 'success'}>
          {message.text}
        </Banner>
      )}

      <PanelCard as="div">
        <Panel autoComplete="off" onSubmit={handleSubmit}>
          {/* hidden dummy fields to prevent browser autofill */}
          <input type="text" name="fake-username" autoComplete="username" style={{ display: 'none' }} />
          <input type="password" name="fake-password" autoComplete="current-password" style={{ display: 'none' }} />

          <FormGrid>
            <FormGroup>
              <Label htmlFor="add-user-firstname">
                <UserIcon size={16} />
                {t('users.firstName')}<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="add-user-firstname"
                type="text"
                placeholder={t('addUser.firstNamePlaceholder')}
                value={form.firstname}
                onChange={(e) => handleChange('firstname', e.target.value)}
                required
                data-cy="add-user-firstname"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="add-user-lastname">
                <UserIcon size={16} />
                {t('users.lastName')}<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="add-user-lastname"
                type="text"
                placeholder={t('addUser.lastNamePlaceholder')}
                value={form.lastname}
                onChange={(e) => handleChange('lastname', e.target.value)}
                required
                data-cy="add-user-lastname"
              />
            </FormGroup>

            <FormGroup className="full-width">
              <Label htmlFor="add-user-email">
                <MailIcon size={16} />
                {t('users.email')}<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="add-user-email"
                type="email"
                placeholder={t('addUser.emailPlaceholder')}
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                data-cy="add-user-email"
              />
            </FormGroup>

            <FormGroup className="full-width">
              <Label htmlFor="add-user-phone">
                <PhoneIcon size={16} />
                {t('yourData.phone')}<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="add-user-phone"
                type="tel"
                placeholder={t('addUser.phonePlaceholder')}
                value={form.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value.replace(/[^\d+ ]/g, ''))}
                data-cy="add-user-phone"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="add-user-password">
                <LockIcon size={16} />
                {t('security.password')}<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="add-user-password"
                type="password"
                placeholder={t('addUser.passwordPlaceholder')}
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                readOnly
                onFocus={e => { e.target.readOnly = false; }}
                data-cy="add-user-password"
              />
              {form.password && (
                <PasswordStrength>
                  <StrengthLabel $tone={passTone}>
                    <span>{t(`passwordStrength.${passScore}`)}</span>
                    <span>{passPercent}%</span>
                  </StrengthLabel>
                  <StrengthBar>
                    <StrengthFill width={passPercent} $tone={passTone} />
                  </StrengthBar>
                  {passEval.feedback && passEval.feedback.suggestions && passEval.feedback.suggestions.length > 0 && (
                    <StrengthTips>
                      <InfoIcon size={16} />
                      <div>
                        {passEval.feedback.warning && <div>{passEval.feedback.warning}</div>}
                        {passEval.feedback.suggestions.join(' • ')}
                      </div>
                    </StrengthTips>
                  )}

                  <Checklist>
                    <ChecklistItem data-ok={policyCheck.details.lenOk}>
                      {policyCheck.details.lenOk ? <CheckIcon size={16} /> : <XIcon size={16} />}
                      <div>{t('addUser.passwordMinLength', { min: policyCheck.details.minLen })}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={policyCheck.details.hasUpper}>
                      {policyCheck.details.hasUpper ? <CheckIcon size={16} /> : <XIcon size={16} />}
                      <div>{t('addUser.passwordUppercase')}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={policyCheck.details.hasLower}>
                      {policyCheck.details.hasLower ? <CheckIcon size={16} /> : <XIcon size={16} />}
                      <div>{t('addUser.passwordLowercase')}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={policyCheck.details.hasDigit}>
                      {policyCheck.details.hasDigit ? <CheckIcon size={16} /> : <XIcon size={16} />}
                      <div>{t('addUser.passwordDigit')}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={passScore >= 3}>
                      {passScore >= 3 ? <CheckIcon size={16} /> : <XIcon size={16} />}
                      <div>{t('addUser.passwordStrengthGood')}</div>
                    </ChecklistItem>
                  </Checklist>
                </PasswordStrength>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="add-user-confirm-password">
                <LockIcon size={16} />
                {t('security.confirmPassword')}<RequiredMark>*</RequiredMark>
              </Label>
              <TextInput
                id="add-user-confirm-password"
                type="password"
                placeholder={t('addUser.confirmPasswordPlaceholder')}
                value={form.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                required
                data-cy="add-user-confirm-password"
              />
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <Banner tone="danger" compact>
                  {t('addUser.passwordMismatch')}
                </Banner>
              )}
            </FormGroup>

            <FormGroup className="full-width">
              <CheckboxWrapper>
                <Checkbox
                  id="add-user-isadmin"
                  type="checkbox"
                  checked={form.isadmin}
                  onChange={(e) => handleChange('isadmin', e.target.checked)}
                />
                <CheckboxLabel as="label" htmlFor="add-user-isadmin">
                  {t('users.admin')} - {t('addUser.adminInfo')}
                </CheckboxLabel>
              </CheckboxWrapper>
            </FormGroup>
          </FormGrid>

          <ButtonGroup>
            <SecondaryButton type="button" onClick={() => navigate('/users')} disabled={loading}>
              <XIcon size={16} />
              {t('common.cancel')}
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={loading || !isFormValid || isDemo}
              title={isDemo ? t('addUser.demoDisabled') : (!isFormValid ? t('addUser.formIncompleteHint') : '')}
              data-cy="add-user-submit"
            >
              <CheckIcon size={16} />
              {loading ? t('common.saving') : t('common.save')}
            </PrimaryButton>
          </ButtonGroup>
        </Panel>
      </PanelCard>
      </TylkoOdczytWPrzerwie>
    </PanelContainer>
  );
}
